import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../lib/i18n';
import { humanizeAuthError } from '../lib/authErrors';
import { CheckCircle2, AlertTriangle, Loader2, Pencil, Eye, EyeOff } from 'lucide-react';

/**
 * Page affichée après que l'utilisateur a cliqué sur le lien de reset
 * envoyé par courriel. Cette page gère elle-même l'échange du code PKCE
 * pour établir la session de récupération (avant on passait par /auth/confirm
 * mais la query string était parfois supprimée par Supabase).
 *
 * Route publique (PAS de <Protected>) pour éviter une race condition.
 */
export default function ResetPassword() {
  const { lang } = useT();
  const { user, loading: authLoading, setSessionFromExchange } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState('form'); // form | success | error
  const [error, setError] = useState('');
  const [exchanging, setExchanging] = useState(true);

  // On mount, exchange the PKCE code from the URL for a session
  useEffect(() => {
    let cancelled = false;

    async function exchangeCode() {
      const queryParams = new URLSearchParams(location.search);
      const hashParams = new URLSearchParams(location.hash.replace(/^#/, ''));

      // Surface error if Supabase rejected the link
      const errDesc = hashParams.get('error_description') || queryParams.get('error_description');
      if (errDesc) {
        if (!cancelled) {
          setError(humanizeAuthError({ message: decodeURIComponent(errDesc.replace(/\+/g, ' ')) }, lang));
          setExchanging(false);
        }
        return;
      }

      const code = queryParams.get('code');
      const tokenHash = queryParams.get('token_hash') || hashParams.get('token_hash');

      try {
        if (code) {
          const { error: exErr } = await supabase.auth.exchangeCodeForSession(code);
          if (exErr) throw exErr;
        } else if (tokenHash) {
          const { error: verErr } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'recovery',
          });
          if (verErr) throw verErr;
        }
        // Wait briefly for the hash-fragment flow (detectSessionInUrl) to settle
        await new Promise((r) => setTimeout(r, 300));
        const { data } = await supabase.auth.getSession();
        if (!cancelled && data.session) {
          setSessionFromExchange(data.session);
        }
        if (!cancelled) {
          // Clean the URL of any sensitive query/hash params
          window.history.replaceState({}, '', '/reset-password');
        }
      } catch (e) {
        if (!cancelled) {
          setError(humanizeAuthError(e, lang));
        }
      } finally {
        if (!cancelled) setExchanging(false);
      }
    }

    exchangeCode();
    return () => { cancelled = true; };
  }, [location, lang, setSessionFromExchange]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!user) {
      setError(lang === 'fr'
        ? 'Votre session de récupération a expiré. Demandez un nouveau lien.'
        : 'Your recovery session has expired. Request a new link.');
      return;
    }
    if (password.length < 8) {
      setError(lang === 'fr' ? 'Le mot de passe doit contenir au moins 8 caractères.' : 'Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError(lang === 'fr' ? 'Les mots de passe ne correspondent pas.' : 'Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const { error: updErr } = await supabase.auth.updateUser({ password });
      if (updErr) throw updErr;
      setState('success');
      setTimeout(() => nav('/', { replace: true }), 1500);
    } catch (e) {
      setError(humanizeAuthError(e, lang));
      setState('error');
    } finally {
      setSubmitting(false);
    }
  };

  // While auth is still being determined, show a small spinner instead of the
  // form (prevents the form from briefly rendering with `!user` and confusing
  // the user).
  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="animate-spin text-[#7A1F2B]" size={36} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="px-4 sm:px-6 h-16 flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-[#7A1F2B] text-white flex items-center justify-center">
          <Pencil size={18} strokeWidth={2.4} />
        </div>
        <span className="font-heading font-black">SoumissionPro</span>
      </div>

      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          {exchanging || authLoading ? (
            <div className="text-center" data-testid="reset-loading">
              <Loader2 className="mx-auto mb-6 animate-spin text-[#7A1F2B]" size={48} />
              <h1 className="font-heading text-3xl font-black tracking-tighter mb-2">
                {lang === 'fr' ? 'Vérification du lien…' : 'Verifying link…'}
              </h1>
              <p className="text-gray-500">
                {lang === 'fr' ? 'Quelques secondes seulement.' : 'Just a few seconds.'}
              </p>
            </div>
          ) : state === 'success' ? (
            <div className="text-center" data-testid="reset-success">
              <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="text-emerald-600" size={36} />
              </div>
              <h1 className="font-heading text-3xl font-black tracking-tighter mb-2">
                {lang === 'fr' ? 'Mot de passe mis à jour !' : 'Password updated!'}
              </h1>
              <p className="text-gray-500">
                {lang === 'fr' ? 'Redirection en cours…' : 'Redirecting…'}
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-password-form">
              <h1 className="font-heading text-3xl font-black tracking-tighter mb-2">
                {lang === 'fr' ? 'Nouveau mot de passe' : 'New password'}
              </h1>
              <p className="text-gray-500 mb-6">
                {lang === 'fr'
                  ? 'Choisissez un nouveau mot de passe pour votre compte.'
                  : 'Choose a new password for your account.'}
              </p>

              {!user && (
                <div data-testid="reset-no-session" className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm flex items-start gap-2">
                  <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>
                    {lang === 'fr'
                      ? "Votre lien de récupération a expiré ou est invalide. Demandez-en un nouveau."
                      : 'Your recovery link has expired or is invalid. Request a new one.'}
                  </span>
                </div>
              )}

              <label className="block text-sm font-semibold mb-1.5">
                {lang === 'fr' ? 'Nouveau mot de passe' : 'New password'}
              </label>
              <div className="relative mb-3">
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="field w-full !pr-12"
                  placeholder={lang === 'fr' ? 'Au moins 8 caractères' : 'At least 8 characters'}
                  autoFocus
                  required
                  minLength={8}
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 z-10"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <label className="block text-sm font-semibold mb-1.5">
                {lang === 'fr' ? 'Confirmer le mot de passe' : 'Confirm password'}
              </label>
              <input
                type={showPwd ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="field w-full mb-4"
                placeholder={lang === 'fr' ? 'Ressaisir le mot de passe' : 'Retype password'}
                required
                minLength={8}
                data-testid="reset-password-confirm"
              />

              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
                  <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full"
                data-testid="reset-password-submit"
              >
                {submitting ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : lang === 'fr' ? 'Mettre à jour mon mot de passe' : 'Update my password'}
              </button>

              <button
                type="button"
                onClick={() => nav('/auth', { replace: true })}
                className="w-full mt-3 py-2.5 text-sm font-medium text-gray-500"
              >
                {lang === 'fr' ? 'Retour à la connexion' : 'Back to sign in'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
